import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Monitor, Package, AlertTriangle, Calendar,
  Clock, DollarSign, Shield, Zap, Users, X, Eye,
  FileText, AlertCircle, Network
} from 'lucide-react';
import { hardwareAssets, saasApps, lifecycleEvents } from '../data/mockData';
import {
  assetAutomationSuggestions,
  assetDependencyGraph,
  assetChangeSpecs,
  saasUserLists
} from '../data/assetAutomationData';
import { DataTable } from '../components/ui/data-table';
import { Button } from '../components/ui/button';

// Risk level colors
const riskColors = {
  low: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' },
  medium: { bg: 'bg-slate-200', text: 'text-slate-700', border: 'border-slate-300' },
  high: { bg: 'bg-slate-900', text: 'text-white', border: 'border-slate-900' }
};

export const Assets = () => {
  const navigate = useNavigate();

  // Selection state
  const [selectedHardware, setSelectedHardware] = useState([]);
  const [selectedSaas, setSelectedSaas] = useState([]);

  // Modal state
  const [dependencyModal, setDependencyModal] = useState(null);
  const [changeSpecModal, setChangeSpecModal] = useState(null);
  const [userListModal, setUserListModal] = useState(null);
  const [exceptionModal, setExceptionModal] = useState(null);

  // Demo state for showing status changes
  const [assetStatuses, setAssetStatuses] = useState({});

  const handleProposeBundle = (suggestionId, targets = []) => {
    const spec = assetChangeSpecs[suggestionId];
    if (spec) {
      navigate('/changes', {
        state: {
          proposeBundle: true,
          changeSpec: spec,
          suggestionId,
          targets
        }
      });
    } else {
      navigate('/changes');
    }
  };

  const handleViewChangeSpec = (suggestionId) => {
    const spec = assetChangeSpecs[suggestionId];
    if (spec) {
      setChangeSpecModal(spec);
    }
  };

  const handleViewDependencies = (assetId) => {
    const deps = assetDependencyGraph[assetId];
    if (deps) {
      setDependencyModal({ assetId, ...deps });
    }
  };

  const handleViewUsers = (appId) => {
    const users = saasUserLists[appId.toLowerCase()];
    if (users) {
      setUserListModal(users);
    }
  };

  const handleBulkAction = (action, type) => {
    const targets = type === 'hardware' ? selectedHardware : selectedSaas;
    switch (action) {
      case 'refresh':
        handleProposeBundle('refresh-servers-3yr', targets);
        break;
      case 'maintenance':
        break;
      case 'decommission':
        break;
      case 'assign':
        break;
      case 'exception':
        setExceptionModal({ targets, type });
        break;
      case 'reclaim':
        handleProposeBundle('reclaim-saas-seats', targets);
        break;
      case 'review':
        break;
      case 'rightsize':
        break;
      default:
        break;
    }
    if (type === 'hardware') setSelectedHardware([]);
    else setSelectedSaas([]);
  };

  const getAssetStatus = (assetId) => {
    return assetStatuses[assetId] || 'Active';
  };

  // Column Definitions
  const hardwareColumns = [
    {
      header: 'Asset',
      accessor: 'asset',
      sortable: true,
      cell: (row) => <span className="font-medium text-slate-900">{row.asset}</span>
    },
    { header: 'Type', accessor: 'type', sortable: true },
    { header: 'Age', accessor: 'age', sortable: true },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getAssetStatus(row.asset) === 'Active' ? 'bg-primary/10 text-primary' :
            getAssetStatus(row.asset) === 'Planned' ? 'bg-slate-100 text-slate-600' :
              getAssetStatus(row.asset) === 'In Progress' ? 'bg-slate-200 text-slate-700' :
                'bg-slate-100 text-slate-600'
          }`}>
          {getAssetStatus(row.asset)}
        </span>
      )
    },
    { header: 'Owner', accessor: 'owner', sortable: true },
    { header: 'Region', accessor: 'region', sortable: true },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleViewDependencies(row.asset)}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
            title="View dependencies"
          >
            <Eye className="w-3 h-3" /> Deps
          </button>
          <button
            onClick={() => handleProposeBundle('refresh-servers-3yr', [row.asset])}
            className="px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded flex items-center gap-1"
            title="Create refresh bundle"
          >
            <Package className="w-3 h-3" /> Bundle
          </button>
          <button
            onClick={() => setExceptionModal({ targets: [row.asset], type: 'hardware' })}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
            title="Create exception"
          >
            <Shield className="w-3 h-3" />
          </button>
        </div>
      )
    }
  ];

  const saasColumns = [
    {
      header: 'Application',
      accessor: 'application',
      sortable: true,
      cell: (row) => <span className="font-medium text-slate-900">{row.application}</span>
    },
    { header: 'Licenses', accessor: 'licenses', sortable: true },
    { header: 'Active', accessor: 'active', sortable: true },
    {
      header: 'Util %',
      accessor: 'utilization',
      sortable: true,
      cell: (row) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.utilization < 50 ? 'bg-slate-200 text-slate-700' : 'bg-primary/10 text-primary'
          }`}>{row.utilization}%</span>
      )
    },
    {
      header: 'Annual Cost',
      accessor: 'annualCost',
      sortable: true,
      cell: (row) => <span>${row.annualCost.toLocaleString()}</span>
    },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleProposeBundle('reclaim-saas-seats', [row.application.toLowerCase()])}
            className="px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded flex items-center gap-1"
            title="Create reclaim bundle"
          >
            <Package className="w-3 h-3" /> Reclaim
          </button>
          <button
            onClick={() => handleViewUsers(row.application)}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
            title="View users"
          >
            <Users className="w-3 h-3" /> Users
          </button>
          <button
            onClick={() => setExceptionModal({ targets: [row.application.toLowerCase()], type: 'saas' })}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
            title="Create exception"
          >
            <Shield className="w-3 h-3" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="assets-page">
      {dependencyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDependencyModal(null)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Network className="w-5 h-5 text-slate-500" />
                <h3 className="font-semibold text-slate-900">Dependencies: {dependencyModal.assetId}</h3>
              </div>
              <button onClick={() => setDependencyModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${riskColors[dependencyModal.blastRadius === 'high' ? 'high' : 'medium'].bg} ${riskColors[dependencyModal.blastRadius === 'high' ? 'high' : 'medium'].text}`}>
                  Blast Radius: {dependencyModal.blastRadius}
                </span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                  {dependencyModal.riskLabel}
                </span>
              </div>

              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Workloads</h4>
                <div className="flex flex-wrap gap-1">
                  {dependencyModal.workloads.map((w, i) => (
                    <span key={i} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">{w}</span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Downstream Dependencies ({dependencyModal.downstream.length})</h4>
                <div className="space-y-2">
                  {dependencyModal.downstream.map((dep, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium text-sm">{dep.name}</span>
                        <span className="text-xs text-slate-500 ml-2">{dep.type}</span>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${dep.criticality === 'P1' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'}`}>
                        {dep.criticality}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-700">Migration complexity: {dependencyModal.migrationComplexity}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => setDependencyModal(null)} className="flex-1">
                Close
              </Button>
              <Button
                onClick={() => { setDependencyModal(null); handleProposeBundle('migration-plan-active', [dependencyModal.assetId]); }}
                className="flex-1"
              >
                Create Migration Bundle
              </Button>
            </div>
          </div>
        </div>
      )}

      {changeSpecModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => setChangeSpecModal(null)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                <h3 className="font-semibold text-slate-900">Change Spec: {changeSpecModal.id}</h3>
              </div>
              <button onClick={() => setChangeSpecModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-slate-100 font-mono whitespace-pre-wrap">
                  {JSON.stringify(changeSpecModal, null, 2)}
                </pre>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t flex gap-2">
              <Button variant="outline" onClick={() => setChangeSpecModal(null)}>
                Close
              </Button>
              <Button
                onClick={() => { setChangeSpecModal(null); navigate('/changes', { state: { proposeBundle: true, changeSpec: changeSpecModal } }); }}
              >
                <Package className="w-4 h-4 mr-2" />
                Propose Action Bundle
              </Button>
            </div>
          </div>
        </div>
      )}

      {userListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setUserListModal(null)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{userListModal.app} - Inactive Users</h3>
                <p className="text-sm text-slate-500">{userListModal.inactive_users.length} of {userListModal.total_users} users inactive</p>
              </div>
              <button onClick={() => setUserListModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[400px]">
              <div className="space-y-2">
                {userListModal.inactive_users.map((user, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{user.email}</p>
                      <p className="text-xs text-slate-500">{user.department}</p>
                    </div>
                    <span className="text-xs text-slate-500">{user.last_login}</span>
                  </div>
                ))}
                <div className="p-3 bg-slate-100 rounded-lg text-center text-sm text-slate-500">
                  + {userListModal.inactive_users.length > 3 ? userListModal.inactive_users.length - 3 : 0} more users
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t">
              <Button variant="outline" onClick={() => setUserListModal(null)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {exceptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setExceptionModal(null)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Create Exception</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Create policy exception for {exceptionModal.targets.length} {exceptionModal.type === 'hardware' ? 'asset(s)' : 'application(s)'}
            </p>
            <div className="space-y-3 mb-6">
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <input type="text" placeholder="Migration scheduled for Q2 2026" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Expiry Date</label>
                <input type="date" defaultValue="2026-06-30" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Owner</label>
                <input type="text" placeholder="Team Lead name" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setExceptionModal(null)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setExceptionModal(null);
                  handleProposeBundle('exception-legacy-web', exceptionModal.targets);
                }}
                className="flex-1"
              >
                Create Exception Bundle
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Server className="w-5 h-5 text-slate-500" />
            Asset Intelligence
          </h1>
          <p className="text-sm text-slate-500 mt-1">Complete inventory of everything IT owns, buys, and runs</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Hardware Assets</span>
            <Server className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">347</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Software Licenses</span>
            <Package className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">1,247</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">SaaS Applications</span>
            <Monitor className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">84</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Alerts</span>
            <AlertTriangle className="w-4 h-4 text-slate-900" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">12</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-900">Suggested Automation</h2>
            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
              {assetAutomationSuggestions.length} actions
            </span>
          </div>
          <span className="text-xs text-slate-500">Intent in, action bundle out, agents execute</span>
        </div>

        <div className="p-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assetAutomationSuggestions.slice(0, 5).map((suggestion) => (
            <div
              key={suggestion.id}
              className="bg-slate-50 rounded-lg border border-slate-100 p-4 hover:border-slate-200 transition-colors"
              data-testid={`suggestion-${suggestion.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-slate-900 text-sm">{suggestion.title}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${riskColors[suggestion.riskLevel].bg} ${riskColors[suggestion.riskLevel].text}`}>
                  {suggestion.riskLevel}
                </span>
              </div>

              <p className="text-xs text-slate-500 mb-4">{suggestion.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600">{suggestion.impact.timeSaved}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <DollarSign className="w-3.5 h-3.5 text-primary" />
                  <span className="text-primary font-medium">{suggestion.impact.costSavings}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Shield className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600">{suggestion.impact.riskReduction}</span>
                </div>
              </div>

              {suggestion.requiredApprovals.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                    <Users className="w-3 h-3" />
                    Required approvals:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {suggestion.requiredApprovals.slice(0, 2).map((approver, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">{approver}</span>
                    ))}
                    {suggestion.requiredApprovals.length > 2 && (
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">+{suggestion.requiredApprovals.length - 2}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => handleProposeBundle(suggestion.id, suggestion.targets)}
                  className="flex-1"
                  data-testid={`create-pr-${suggestion.id}`}
                >
                  <Package className="w-3.5 h-3.5 mr-1" />
                  Propose Bundle
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewChangeSpec(suggestion.id)}
                >
                  View Spec
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Hardware Assets</h2>
          <span className="text-xs text-slate-500">Servers in us-east-1 older than 3 years</span>
        </div>
        <div className="p-4">
          <DataTable
            columns={hardwareColumns}
            data={hardwareAssets}
            keyField="asset"
            onSelectionChange={setSelectedHardware}
            actions={selectedHardware.length > 0 && (
              <>
                <Button size="sm" onClick={() => handleBulkAction('refresh', 'hardware')}>
                  <Package className="w-3.5 h-3.5 mr-1" /> Propose Refresh Bundle
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('maintenance', 'hardware')}>
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Maintenance
                </Button>
              </>
            )}
          />

          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
            <h3 className="font-medium text-slate-900 mb-3 text-sm">Recommendations</h3>
            <ul className="space-y-1 text-sm text-slate-600 mb-4">
              <li>3 servers past refresh cycle (3 year policy)</li>
              <li>Estimated replacement cost: $45,000</li>
              <li>2 servers have active workloads requiring migration plan</li>
            </ul>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleProposeBundle('refresh-servers-3yr')}>
                <Package className="w-4 h-4 mr-1" /> Propose Refresh Bundle
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleViewDependencies('db-server-01')}>
                <Eye className="w-4 h-4 mr-1" /> View Dependencies
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Underutilized SaaS</h2>
          <span className="text-xs text-slate-500">Applications with less than 50% utilization</span>
        </div>
        <div className="p-4">
          <DataTable
            columns={saasColumns}
            data={saasApps}
            keyField="application"
            onSelectionChange={(ids) => setSelectedSaas(ids.map(id => id.toLowerCase()))}
            actions={selectedSaas.length > 0 && (
              <>
                <Button size="sm" onClick={() => handleBulkAction('reclaim', 'saas')}>
                  <Package className="w-3.5 h-3.5 mr-1" /> Reclaim
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('review', 'saas')}>
                  <Users className="w-3.5 h-3.5 mr-1" /> Review
                </Button>
              </>
            )}
          />

          <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
            <p className="text-sm font-medium text-primary">
              Potential Savings: $51,000/year (right-size to actual usage)
            </p>
            <Button
              onClick={() => handleProposeBundle('reclaim-saas-seats')}
            >
              <Package className="w-4 h-4 mr-1" /> Propose Reclaim Bundle
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Lifecycle Events - Next 90 Days</h2>
          <Calendar className="w-4 h-4 text-slate-400" />
        </div>
        <div className="p-4 grid md:grid-cols-3 gap-4">
          {lifecycleEvents.map((category, idx) => (
            <div key={idx} className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
              <div className="px-4 py-2 bg-slate-100 border-b border-slate-200">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{category.type}</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {category.items.map((item, i) => (
                  <div key={i} className="px-4 py-3 hover:bg-slate-100 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{item.name}</p>
                        {item.count && <p className="text-xs text-slate-500">{item.count}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{item.date}</p>
                        {item.cost && <p className="text-xs font-medium text-slate-700">{item.cost}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
